import React, { useState } from 'react';
import { X, Search, FileText, Plus, Trash2, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function PromptLibrary({ onClose, onSelect }) {
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    React.useEffect(() => {
        fetchPrompts();
    }, []);

    const fetchPrompts = async () => {
        try {
            const response = await fetch(`${API}/prompts`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setPrompts(data);
            }
        } catch (error) {
            toast.error('Failed to load prompts');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!newTitle.trim() || !newContent.trim()) {
            toast.error('Title and content required');
            return;
        }

        try {
            const response = await fetch(`${API}/prompts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    title: newTitle,
                    content: newContent,
                    category: newCategory || 'General'
                })
            });

            if (response.ok) {
                const newPrompt = await response.json();
                setPrompts([...prompts, newPrompt]);
                setShowCreate(false);
                setNewTitle('');
                setNewContent('');
                setNewCategory('');
                toast.success('Prompt created');
            }
        } catch (error) {
            toast.error('Failed to create prompt');
        }
    };

    const handleDelete = async (e, promptId) => {
        e.stopPropagation();
        if (!window.confirm('Delete this prompt?')) return;

        try {
            const response = await fetch(`${API}/prompts/${promptId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                setPrompts(prompts.filter(p => p.prompt_id !== promptId));
                toast.success('Prompt deleted');
            }
        } catch (error) {
            toast.error('Failed to delete prompt');
        }
    };

    const filteredPrompts = prompts.filter(prompt => {
        const matchesSearch = prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            prompt.content.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || prompt.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const categories = ['All', ...new Set(prompts.map(p => p.category || 'General'))];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="glass-card w-full max-w-4xl h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-accent-cyan/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-accent-cyan" />
                        </div>
                        <div>
                            <h2 className="font-heading text-xl font-bold">Prompt Library</h2>
                            <p className="text-sm text-text-secondary">Select or create prompts for your nodes</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Sidebar */}
                    <div className="w-64 border-r border-white/5 p-4 overflow-y-auto">
                        <button
                            onClick={() => setShowCreate(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary-hover transition-colors mb-6 font-medium"
                        >
                            <Plus className="w-4 h-4" />
                            New Prompt
                        </button>

                        <div className="space-y-1">
                            <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2 px-2">Categories</p>
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedCategory === category
                                            ? 'bg-white/10 text-white font-medium'
                                            : 'text-text-secondary hover:bg-white/5 hover:text-white'
                                        }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Main List */}
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Search Bar */}
                        <div className="p-4 border-b border-white/5">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                                <input
                                    type="text"
                                    placeholder="Search prompts..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="glass-input w-full pl-10"
                                />
                            </div>
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto p-4 grid gap-4">
                            {showCreate ? (
                                <div className="bg-white/5 rounded-xl p-6 border border-white/10 animate-fade-in">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-heading text-lg font-bold">Create New Prompt</h3>
                                        <button
                                            onClick={() => setShowCreate(false)}
                                            className="text-sm text-text-secondary hover:text-white"
                                        >
                                            Cancel
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Title</label>
                                            <input
                                                type="text"
                                                value={newTitle}
                                                onChange={(e) => setNewTitle(e.target.value)}
                                                className="glass-input w-full"
                                                placeholder="e.g., System Architecture Template"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Category</label>
                                            <input
                                                type="text"
                                                value={newCategory}
                                                onChange={(e) => setNewCategory(e.target.value)}
                                                className="glass-input w-full"
                                                placeholder="e.g., Architecture"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">Content</label>
                                            <textarea
                                                value={newContent}
                                                onChange={(e) => setNewContent(e.target.value)}
                                                className="glass-input w-full h-32 resize-none"
                                                placeholder="Enter the prompt content here..."
                                            />
                                        </div>

                                        <button
                                            onClick={handleCreate}
                                            className="neon-button w-full justify-center"
                                        >
                                            Save Prompt
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {loading ? (
                                        <div className="flex items-center justify-center h-full">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                        </div>
                                    ) : filteredPrompts.length === 0 ? (
                                        <div className="text-center py-12 text-text-muted">
                                            <p>No prompts found</p>
                                        </div>
                                    ) : (
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {filteredPrompts.map(prompt => (
                                                <div
                                                    key={prompt.prompt_id}
                                                    onClick={() => {
                                                        if (onSelect) onSelect(prompt);
                                                        else {
                                                            navigator.clipboard.writeText(prompt.content);
                                                            toast.success('Copied to clipboard');
                                                        }
                                                    }}
                                                    className="group bg-white/5 hover:bg-white/10 border border-white/5 hover:border-primary/50 rounded-xl p-4 cursor-pointer transition-all relative"
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <span className="px-2 py-1 rounded bg-black/40 text-xs font-medium text-accent-cyan border border-white/5">
                                                            {prompt.category || 'General'}
                                                        </span>
                                                        {!prompt.is_template && (
                                                            <button
                                                                onClick={(e) => handleDelete(e, prompt.prompt_id)}
                                                                className="p-1 hover:bg-white/10 rounded text-text-muted hover:text-accent-pink transition-colors opacity-0 group-hover:opacity-100"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        {prompt.is_template && (
                                                            <Globe className="w-4 h-4 text-text-muted opacity-50" />
                                                        )}
                                                    </div>

                                                    <h4 className="font-heading font-medium mb-2 truncate pr-6">{prompt.title}</h4>
                                                    <p className="text-sm text-text-secondary line-clamp-3 font-body">{prompt.content}</p>

                                                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-end text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                                        Click to select
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PromptLibrary;
